import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Tag, message, Alert } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons/faArrowLeft';
import { getLang } from 'shared/store/selectors/lang.selector';
import { formatFullName, formatNumber } from 'shared/utils';
import { useProfileLang } from 'shared/hooks/profile.hooks';
import {
  EVENTS,
  SCHEDULE_ANALYTICS_VIEW_TYPE,
  SCHEDULE_REPORT_EXPORT_TYPE,
} from 'shared/constants';
import { Button } from 'src/components/design-system';
import { Loader } from 'src/components/core';
import analyticsService from 'src/helpers/analytics.service';
import monitorService from 'src/helpers/monitor.service';
import { AG_FILTER_TYPES, textWithFallback } from 'src/components/design-system/table';
import scheduleApi from 'shared/api/schedule.service.api';
import { usePersonAttendance } from 'shared/hooks/schedule-events.hooks';
import {
  attendanceModeLabel,
  getCheckInMethodLabel,
  eventDetailsPath,
  exportErrorMessage,
} from '../../helpers/event-view.helpers';
import { useEducationalEventFormat } from '../../helpers/use-educational-event-format';
import { downloadBlob } from '../../helpers/download-blob';
import { REPORT_EXPORT_FILE, buildReportFileName } from '../../helpers/report-file-name';
import { buildReportExportedProps } from '../../helpers/report-exported-props';
import { ReportExportButton } from './report-export-button';
import { ReportDateRange } from './report-date-range';
import { ReportTable, SET_FILTER, RESETTABLE_FILTER } from './report-table';
import { ReportLinkCell } from './report-link-cell';
import styles from './reports.module.scss';

export function PersonDrillDown({
  userId,
  apiRange,
  from,
  until,
  onRangeChange,
  onSelectEvent,
  onBack,
}) {
  const lang = useSelector(getLang('EDUCATIONAL_EVENTS'));
  const { getValueFromLang } = useProfileLang();
  const { formatDayWithWeekday, formatDateWithTime, formatTime } = useEducationalEventFormat();

  const query = usePersonAttendance(userId, apiRange);
  const data = query.data;
  const rows = data?.items ?? [];

  const columns = useMemo(
    () => [
      {
        headerName: lang.REPORT_COL_EVENT,
        colId: 'eventTitle',
        field: 'eventTitle',
        sortable: true,
        flex: 2,
        cellClass: styles.primaryCell,
        valueFormatter: textWithFallback,
        cellRenderer: ReportLinkCell,
        cellRendererParams: {
          getTo: data =>
            data?.eventId ? eventDetailsPath(data.eventId, { fromReport: true }) : null,
        },
        filter: AG_FILTER_TYPES.TEXT,
        filterParams: RESETTABLE_FILTER,
      },
      {
        headerName: lang.REPORT_COL_DATE,
        colId: 'eventStartTime',
        sortable: true,
        sort: 'desc',
        flex: 1,
        valueGetter: params => params.data?.eventStartTime ?? null,
        cellRenderer: params =>
          params.value ? formatDateWithTime(params.value) : textWithFallback({ value: null }),
        filter: AG_FILTER_TYPES.DATE,
        filterParams: RESETTABLE_FILTER,
        filterValueGetter: params =>
          params.data?.eventStartTime ? new Date(params.data.eventStartTime) : null,
        comparator: (a, b) => new Date(a || 0).getTime() - new Date(b || 0).getTime(),
      },
      {
        headerName: lang.REPORT_COL_TYPE,
        colId: 'attendanceMode',
        sortable: true,
        flex: 1,
        valueGetter: params => params.data?.attendanceMode,
        cellRenderer: params =>
          params.value ? (
            <Tag className={styles.typeTag} variant="filled">
              {attendanceModeLabel(params.value, lang)}
            </Tag>
          ) : (
            '-'
          ),
        filter: SET_FILTER,
        filterParams: { valueFormatter: value => attendanceModeLabel(value, lang) },
      },
      {
        headerName: lang.REPORT_COL_TIME,
        colId: 'checkedInAt',
        sortable: true,
        flex: 1,
        field: 'checkedInAt',
        cellRenderer: params => (params.value ? formatTime(params.value) : '-'),
        filter: AG_FILTER_TYPES.TEXT,
        filterParams: RESETTABLE_FILTER,
        filterValueGetter: params =>
          params.data?.checkedInAt ? formatTime(params.data.checkedInAt) : '',
      },
      {
        headerName: lang.CHECK_IN_METHOD,
        colId: 'method',
        sortable: true,
        flex: 1,
        valueGetter: params => params.data?.method,
        cellRenderer: params => {
          const label = getCheckInMethodLabel({ method: params.value, lang });
          return label ? (
            <Tag className={styles.typeTag} variant="filled">
              {label}
            </Tag>
          ) : (
            '-'
          );
        },
        filter: SET_FILTER,
        filterParams: {
          valueFormatter: value => getCheckInMethodLabel({ method: value, lang }),
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang, formatDateWithTime, formatTime],
  );

  const handleExport = async () => {
    try {
      const { blob } = await scheduleApi.exportPersonReport({ userId, ...apiRange });
      downloadBlob(
        blob,
        buildReportFileName(formatFullName(data?.user), REPORT_EXPORT_FILE.PERSON_SUFFIX),
      );
      analyticsService.track(
        EVENTS.SCHEDULE.REPORT_EXPORTED,
        buildReportExportedProps({
          reportType: SCHEDULE_REPORT_EXPORT_TYPE.PERSON,
          viewType: SCHEDULE_ANALYTICS_VIEW_TYPE.BY_PERSON,
          providerId: userId,
          from: apiRange.from,
          until: apiRange.until,
        }),
      );
    } catch (error) {
      monitorService.error('Failed to export person attendance report', { error, userId });
      message.error(exportErrorMessage(error, lang));
    }
  };

  const summary = data?.user
    ? lang.REPORT_PERSON_SUMMARY.replace(
        '{role}',
        data.user.isActiveMember
          ? getValueFromLang(data.user.role) || '—'
          : lang.REPORT_DEACTIVATED_USER,
      )
        .replace('{count}', formatNumber(data.user.eventsAttended ?? 0))
        .replace(
          '{date}',
          data.user.lastAttendedAt ? formatDayWithWeekday(data.user.lastAttendedAt) : '—',
        )
    : '';

  const renderContent = () => {
    if (query.isLoading) return <Loader />;
    if (query.isError) {
      return <Alert type="error" showIcon message={lang.REPORT_LOAD_FAILED} />;
    }
    return (
      <ReportTable
        columnDefs={columns}
        rowData={rows}
        getRowId={params => params.data.checkInId}
        onRowActivate={row => row?.eventId && onSelectEvent(row.eventId)}
      />
    );
  };

  return (
    <div className={styles.drillDown}>
      <div className={styles.filterBar}>
        <div className={styles.drillDownTitle}>
          <Button type="link" size="small" className={styles.backLink} onClick={onBack}>
            <FontAwesomeIcon icon={faArrowLeft} /> {lang.REPORT_BACK_TO_REPORTS}
          </Button>
          <span className={styles.personName}>{formatFullName(data?.user)}</span>
          <span className={styles.drillDownSummary}>{summary}</span>
        </div>
        <div className={styles.filterSpacer} />
        <ReportDateRange from={from} until={until} onChange={onRangeChange} />
        <ReportExportButton
          onExport={handleExport}
          lang={lang}
          disabled={!rows.length || query.isError}
        />
      </div>

      {renderContent()}
    </div>
  );
}
